import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { clicksAPI, allowlistAPI } from '../utils/api';

const REFRESH_MS = 120000; // 2 min

const REASON_LABELS = {
  vpn_detected: 'VPN Software',
  tor_detected: 'Tor Network',
  proxy_detected: 'Proxy Detected',
  datacenter_ip: 'Datacenter IP',
  excessive_clicks_10min: 'Over threshold (10 min)',
  excessive_hourly_clicks: 'Over threshold (1 hr)',
  excessive_daily_clicks: 'Over threshold (24 hrs)',
  excessive_weekly_clicks: 'Over threshold (7 days)',
  excessive_monthly_clicks: 'Over threshold (30 days)',
  excessive_clicks_rule1: 'Over threshold (rule 1)',
  excessive_clicks_rule2: 'Over threshold (rule 2)',
  excessive_clicks_rule3: 'Over threshold (rule 3)',
  suspicious_user_agent: 'Suspicious User Agent',
  crawler_detected: 'Bot / Crawler',
  suspicious_ip_range: 'Suspicious IP Range',
  ip_range_abuse: 'IP Range Abuse',
  no_referrer: 'No Referrer',
  previously_blocked: 'Previously Blocked',
  short_time_on_site: 'Bounced Immediately',
  bot_like_behavior: 'Bot-like Behaviour',
  device_id_multiple_ips: 'Device — Multiple IPs',
  device_id_excessive_clicks: 'Device — Excessive Clicks',
  cross_domain_blocked: 'Blocked on Another Campaign',
  country_blocked: 'Country Blocked',
  country_not_allowed: 'Country Not Allowed',
  manually_excluded: 'Manually Excluded',
  manual_block: 'Manually Blocked',
};

const REASON_COLORS = ['#f85149', '#e3b341', '#58a6ff', '#3fb950', '#bc8cff', '#ff7b72', '#79c0ff', '#56d364'];

function reasonLabel(r) {
  return REASON_LABELS[r] || r.replace(/_/g, ' ');
}

function downloadCSV(rows) {
  const header = 'IP Address,Country,Browser,Device,Score,Reason,Blocked At';
  const lines = rows.map(r =>
    `"${r.ip_address || r.ip}","${r.country || ''}","${r.browser || ''}","${r.device_type || ''}","${r.fraud_score ?? ''}","${r.reason || ''}","${new Date(r.created_at).toLocaleString()}"`
  );
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `blocked-ips-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function BlockedIPs({ campaignId }) {
  const [blocked, setBlocked] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  // Add IP modal
  const [showModal, setShowModal] = useState(false);
  const [newIP, setNewIP] = useState('');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');

  // Allow-list tab state
  const [tab, setTab] = useState('blocked');
  const [allowlist, setAllowlist] = useState([]);
  const [allowLoading, setAllowLoading] = useState(false);
  const [allowIP, setAllowIP] = useState('');
  const [allowNote, setAllowNote] = useState('');
  const [allowAdding, setAllowAdding] = useState(false);
  const [allowError, setAllowError] = useState('');

  const reloadAllowlist = async () => {
    if (!campaignId) return;
    setAllowLoading(true);
    try { setAllowlist(await allowlistAPI.list(campaignId)); }
    catch { setAllowlist([]); }
    setAllowLoading(false);
  };
  useEffect(() => { if (tab === 'allowlist') reloadAllowlist(); }, [tab, campaignId]); // eslint-disable-line

  const addAllowlistEntry = async () => {
    setAllowError('');
    const ip = allowIP.trim();
    if (!ip) { setAllowError('Enter an IP or CIDR'); return; }
    setAllowAdding(true);
    try {
      await allowlistAPI.add(campaignId, { ip_address: ip, note: allowNote.trim() });
      setAllowIP(''); setAllowNote('');
      await reloadAllowlist();
    } catch (e) {
      setAllowError(e?.response?.data?.detail || 'Failed to add');
    }
    setAllowAdding(false);
  };

  const removeAllowlistEntry = async (id) => {
    if (!window.confirm('Remove this allow-list entry?')) return;
    try { await allowlistAPI.remove(campaignId, id); await reloadAllowlist(); }
    catch { /* ignore */ }
  };

  useEffect(() => {
    if (!campaignId) { setBlocked([]); return; }
    let cancelled = false;
    const fetchData = async () => {
      if (!cancelled) setLoading(true);
      try {
        const data = await clicksAPI.getBlocked(campaignId);
        if (!cancelled) { setBlocked(data); setLastUpdated(new Date()); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setBlocked([]);
    fetchData();
    const timer = setInterval(fetchData, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [campaignId]);

  const handleUnblock = async (ip) => {
    if (!window.confirm(`Unblock ${ip}?`)) return;
    await clicksAPI.unblock(campaignId, ip);
    setBlocked(prev => prev.filter(b => (b.ip_address || b.ip) !== ip));
  };

  const handleAddIP = async (e) => {
    e.preventDefault();
    setAddError('');
    setAdding(true);
    try {
      const doc = await clicksAPI.blockIP(campaignId, newIP.trim(), newNote.trim());
      setBlocked(prev => [doc, ...prev]);
      setShowModal(false);
      setNewIP('');
      setNewNote('');
    } catch (err) {
      setAddError(err?.response?.data?.detail || 'Failed to block IP. Check the format and try again.');
    } finally {
      setAdding(false);
    }
  };

  // Reason breakdown pie
  const reasonCounts = {};
  blocked.forEach(b => {
    (b.reason || '').split(', ').forEach(r => {
      r = r.trim();
      if (r) reasonCounts[r] = (reasonCounts[r] || 0) + 1;
    });
  });
  const pieData = Object.entries(reasonCounts)
    .map(([name, value]) => ({ name, label: reasonLabel(name), value }))
    .sort((a, b) => b.value - a.value);

  const filtered = blocked.filter(b => {
    if (!search) return true;
    const ip = b.ip_address || b.ip || '';
    return ip.includes(search) || (b.reason || '').toLowerCase().includes(search.toLowerCase());
  });

  if (!campaignId) {
    return (
      <div>
        <div className="page-header"><h1>Blocked IPs</h1><p>Auto-blocked fraud IPs</p></div>
        <div className="empty">
          <h3>No campaign selected</h3>
          <p>Select a campaign from the sidebar to see blocked IPs.</p>
          <NavLink to="/campaigns" className="btn btn-primary" style={{ marginTop: 12, display: 'inline-block' }}>
            Go to Campaigns →
          </NavLink>
        </div>
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1>Blocked IPs</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {blocked.length} IPs currently blocked
            {lastUpdated && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>
                · updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '7px 16px' }}
            onClick={() => { setShowModal(true); setAddError(''); setNewIP(''); setNewNote(''); }}
          >
            + Block IP
          </button>
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '7px 14px' }}
            onClick={() => downloadCSV(blocked)}
            disabled={blocked.length === 0}
          >
            ↓ Download CSV
          </button>
          <div style={{ padding: '10px 16px', background: 'rgba(248,81,73,0.1)', border: '1px solid rgba(248,81,73,0.25)', borderRadius: 8, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 22, color: 'var(--accent-red)', fontWeight: 700, textAlign: 'center' }}>
            {blocked.length}<div style={{ fontSize: 10, color: 'var(--text-muted)' }}>BLOCKED</div>
          </div>
        </div>
      </div>

      {/* Tab bar: Blocked / Allow-list */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[
          { id: 'blocked', label: `Blocked (${blocked.length})` },
          { id: 'allowlist', label: `Allow-list${allowlist.length ? ` (${allowlist.length})` : ''}` },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', padding: '10px 20px', fontSize: 13, fontWeight: 500,
            cursor: 'pointer', whiteSpace: 'nowrap',
            color: tab === t.id ? 'var(--accent-blue)' : 'var(--text-secondary)',
            borderBottom: `2px solid ${tab === t.id ? 'var(--accent-blue)' : 'transparent'}`,
            marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'allowlist' && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="section-title">Allow-listed IPs — never auto-blocked, never pushed to Google Ads</div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
            Use for office IPs, VPN ranges your team uses, test traffic, or trusted partners. Accepts a single IP (<code>203.0.113.42</code>) or a CIDR range (<code>203.0.113.0/24</code>).
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input type="text" placeholder="IP or CIDR" value={allowIP} onChange={e => setAllowIP(e.target.value)}
              style={{ flex: '1 1 180px', fontSize: 13, padding: '9px 12px', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }} />
            <input type="text" placeholder="Note (optional) — e.g. Office NYC" value={allowNote} onChange={e => setAllowNote(e.target.value)}
              style={{ flex: '2 1 240px', fontSize: 13, padding: '9px 12px' }} />
            <button className="btn btn-primary" disabled={allowAdding} onClick={addAllowlistEntry} style={{ fontSize: 12, padding: '8px 18px' }}>
              {allowAdding ? 'Adding…' : '+ Add'}
            </button>
          </div>
          {allowError && (
            <div style={{ padding: '8px 12px', background: 'rgba(248,81,73,0.08)', border: '1px solid rgba(248,81,73,0.25)', borderRadius: 8, fontSize: 12, color: 'var(--accent-red)', marginBottom: 10 }}>
              {allowError}
            </div>
          )}
          {allowLoading ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 16, textAlign: 'center' }}>Loading…</div>
          ) : allowlist.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: 20, textAlign: 'center', background: 'var(--bg-secondary)', borderRadius: 8 }}>
              No allow-listed IPs. Add one above to prevent accidental blocking of trusted traffic.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                  <th style={{ padding: '8px 10px', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>IP / CIDR</th>
                  <th style={{ padding: '8px 10px', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Note</th>
                  <th style={{ padding: '8px 10px', fontWeight: 600, fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Added</th>
                  <th style={{ padding: '8px 10px' }} />
                </tr>
              </thead>
              <tbody>
                {allowlist.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 10px', fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}>{row.ip_address}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-secondary)' }}>{row.note || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td style={{ padding: '8px 10px', color: 'var(--text-muted)', fontSize: 12 }}>{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      <button onClick={() => removeAllowlistEntry(row.id)}
                        style={{ fontSize: 11, padding: '4px 10px', background: 'transparent', color: 'var(--accent-red)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Reason breakdown pie */}
      {tab === 'blocked' && pieData.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="section-title">Block Reason Breakdown</div>
          <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />)}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [value, props.payload.label]}
                  contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1, minWidth: 200 }}>
              {pieData.map((d, i) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: REASON_COLORS[i % REASON_COLORS.length], flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.label}</span>
                  </div>
                  <span style={{ fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 12, fontWeight: 600 }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'blocked' && blocked.length === 0 ? (
        <div className="empty">
          <h3>No blocked IPs</h3>
          <p>IPs will be automatically blocked here when fraud is detected.</p>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => { setShowModal(true); setAddError(''); setNewIP(''); setNewNote(''); }}>
            + Block an IP manually
          </button>
        </div>
      ) : tab === 'blocked' ? (
        <div className="table-wrap">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
            <input
              placeholder="Search by IP or reason..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 320, fontSize: 13 }}
            />
          </div>
          <table>
            <thead>
              <tr>
                <th>IP Address</th>
                <th>Country</th>
                <th>Browser / Device</th>
                <th>Score</th>
                <th>Detection Reason</th>
                <th>Blocked At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => {
                const ip = b.ip_address || b.ip;
                const reasons = (b.reason || '').split(', ').filter(Boolean);
                const score = b.fraud_score;
                const scoreColor = score >= 70 ? 'var(--accent-red)' : score >= 40 ? 'var(--accent-orange)' : 'var(--accent-green)';
                return (
                  <tr key={i}>
                    <td><span className="ip-mono">{ip}</span></td>
                    <td style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {b.country_code ? (
                        <span title={b.country}>
                          {b.country_code} {b.country ? `· ${b.country}` : ''}
                        </span>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {b.browser && <div style={{ fontWeight: 500 }}>{b.browser}</div>}
                      {b.device_type && (
                        <div style={{ color: 'var(--text-muted)', textTransform: 'capitalize', marginTop: 1 }}>{b.device_type}</div>
                      )}
                      {!b.browser && !b.device_type && <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>
                      {score != null ? (
                        <span style={{
                          fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700, color: scoreColor,
                        }}>{score}</span>
                      ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {reasons.map(r => (
                          <span key={r} className="badge fraud" style={{ fontSize: 10 }}>{reasonLabel(r)}</span>
                        ))}
                        {reasons.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 11, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {b.created_at ? new Date(b.created_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <button className="btn btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => handleUnblock(ip)}>
                        Unblock
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No results match your search.
            </div>
          )}
        </div>
      ) : null}

      {/* Add IP Modal */}
      {showModal && (
        <div
          onClick={e => e.target === e.currentTarget && setShowModal(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div className="card" style={{ width: '100%', maxWidth: 420, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Block IP Address</h2>
              <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 16 }} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <form onSubmit={handleAddIP} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  IP Address <span style={{ color: 'var(--accent-red)' }}>*</span>
                </label>
                <input
                  required
                  autoFocus
                  placeholder="e.g. 192.168.1.1"
                  value={newIP}
                  onChange={e => setNewIP(e.target.value)}
                  style={{ fontSize: 13, fontFamily: 'var(--font-body)', fontVariantNumeric: 'tabular-nums' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                  Reason / Note <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>(optional)</span>
                </label>
                <input
                  placeholder="e.g. competitor, manual review..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  style={{ fontSize: 13 }}
                />
              </div>

              {addError && (
                <div style={{ padding: '10px 14px', background: 'rgba(248,81,73,0.1)', border: '1px solid rgba(248,81,73,0.3)', borderRadius: 8, fontSize: 13, color: 'var(--accent-red)' }}>
                  {addError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={adding}>
                  {adding ? 'Blocking…' : 'Block IP'}
                </button>
              </div>
            </form>

            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 16, lineHeight: 1.6 }}>
              This IP will be added to the block list immediately and synced to Google Ads if auto-sync is enabled.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
